import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiPaginationMetadata } from "@skailer-com/uikit/core";
import {
  ApiParticipantChartAxisType,
  ApiParticipantChartConfig,
  ApiParticipantChartConfigAxisConfig,
  ApiPerformanceReviewParticipantChartSearchItem,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import {
  Box,
  Button,
  EmptyStateBase,
  PaginationBlock,
  Stack,
  Table,
  Text,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface PerformanceReviewInsightChartTableProps {
  performanceReviewId: number;
  items: ApiPerformanceReviewParticipantChartSearchItem[];
  config: ApiParticipantChartConfig | undefined;
  isLoading: boolean;
  isFetching: boolean;
  metadata?: ApiPaginationMetadata;
  onPageChange: (page: number) => void;
  employeeIds?: number[];
  onClearEmployeeFilter: () => void;
}

export function PerformanceReviewInsightChartTable({
  performanceReviewId,
  items,
  config,
  isLoading,
  isFetching,
  metadata,
  onPageChange,
  employeeIds,
  onClearEmployeeFilter,
}: PerformanceReviewInsightChartTableProps) {
  const { t } = useTranslation();

  const hasEmployeeFilter = employeeIds && employeeIds.length > 0;

  const columnHelper = createColumnHelper<ApiPerformanceReviewParticipantChartSearchItem>();

  const columns = useMemo<ColumnDef<ApiPerformanceReviewParticipantChartSearchItem>[]>(() => {
    if (!config) return [];

    const inner: DisplayColumnDef<ApiPerformanceReviewParticipantChartSearchItem>[] = [];

    inner.push(
      columnHelper.display({
        id: "employee",
        header: t("core:full_name"),
        cell: (cell) => <EmployeeItem enablePreview employee={cell.row.original.employee} />,
        ...ColumnSizes.employee,
      }),
      columnHelper.display({
        id: "rating",
        header: t("performance_review:rating"),
        cell: (cell) => (
          <Box className={"pr-8"}>
            <Box className={twMerge("h-1 rounded-xs bg-dark/10 w-full overflow-hidden relative")}>
              <Box
                style={{
                  width:
                    cell.row.original.rating.max == 0
                      ? 0
                      : (cell.row.original.rating.value / cell.row.original.rating.max) * 100 + "%",
                }}
                className={twMerge("absolute left-0 h-full rounded-xs bg-brand-dark")}
              ></Box>
            </Box>
          </Box>
        ),
        ...ColumnSizes.narrow,
      })
    );

    const buildAxisColumn = (
      axis: ApiParticipantChartConfigAxisConfig,
      axisKey: "x_axis" | "y_axis"
    ): DisplayColumnDef<ApiPerformanceReviewParticipantChartSearchItem> | null => {
      if (
        ![
          ApiParticipantChartAxisType.overallReview,
          ApiParticipantChartAxisType.reviewerType,
        ].includes(axis.type)
      ) {
        return null;
      }

      const header =
        axis.type === ApiParticipantChartAxisType.reviewerType && axis.reviewer_type
          ? (PerformanceReviewReviewerTypeItems().find(
              (item) => item.id == axis.reviewer_type!.type
            )?.title ??
            axis.reviewer_type.title ??
            t("common:no_title"))
          : t("performance_review:overall");

      return columnHelper.display({
        id: axisKey,
        header,
        cell: (cell) => (
          <Text>{cell.row.original[axisKey]?.scale_score?.avg_scale_score?.toFixed(1) ?? "-"}</Text>
        ),
        ...ColumnSizes.narrow,
      });
    };

    const xAxisColumn = buildAxisColumn(config.x_axis, "x_axis");
    if (xAxisColumn) inner.push(xAxisColumn);

    if (config.x_axis.type != config.y_axis.type) {
      const yAxisColumn = buildAxisColumn(config.y_axis, "y_axis");
      if (yAxisColumn) inner.push(yAxisColumn);
    }

    inner.push(
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (cell) => (
          <Stack className={"items-end"}>
            <Button
              variant={"secondary"}
              onClick={() => {
                window.open(
                  `/performance-reviews/${performanceReviewId}/view/participants/${cell.row.original.employee.id}`,
                  "_blank"
                );
              }}
            >
              {t("common:view")}
            </Button>
          </Stack>
        ),
        ...ColumnSizes.actions,
      })
    );

    return inner;
  }, [config, performanceReviewId]);

  return (
    <>
      <Table
        data={items}
        columns={columns}
        stickyLastColumn
        showSkeleton={isLoading}
        isFetching={isFetching}
        showNotFoundError={metadata?.total_items == 0}
        notFoundComponent={<EmptyStateNothingFound />}
        underHeadSlotFullWidth
        underHeadSlot={
          hasEmployeeFilter && (metadata?.total_items ?? 0) > 0 ? (
            <EmptyStateBase
              variant="plain"
              title={t("plurals:employees_filtered", {
                count: metadata?.total_items,
                employees: t("plurals:employees_count", { count: metadata?.total_items }),
              })}
              primaryButton={
                <Button onClick={onClearEmployeeFilter}>{t("core:clear_selection")}</Button>
              }
            />
          ) : undefined
        }
      />
      <PaginationBlock metadata={metadata} onPageChange={onPageChange} />
    </>
  );
}
